import { useEffect, useRef, useState } from "react";
import axios from 'axios';
import PAYS_OPTIONS from "./mandatPaysOptions";
const { buildQuestionLookup, isQuestionVisible } = require("../shared/progress");

function QuestionMandatSEPA({ questionInfo, etudiant, updateQuestionValue, sousCritereIndex, questionIndex, getNumberFiliereActif, getNumberCritereActif, data, Lang, setQuestionValueToTrue }) {

    const [fileName] = useState(questionInfo.nom + ".png");
    const [fileURL, setFileURL] = useState(null);
    const [isErrorVisible, setIsErrorVisible] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [downloadError, setDownloadError] = useState('');
    const [payeurGarantStatus, setPayeurGarantStatus] = useState('');
    const [formValues, setFormValues] = useState({
        nom: '',
        prenom: '',
        adresse: '',
        codePostal: '',
        ville: '',
        pays: '',
        iban: '',
        bic: '',
        villeSignature: ''
    });
    const [formErrors, setFormErrors] = useState({
        nom: '',
        prenom: '',
        adresse: '',
        codePostal: '',
        ville: '',
        pays: '',
        iban: '',
        bic: '',
        villeSignature: ''
    });
    const [villes, setVilles] = useState([]);
    const [citySuggestions, setCitySuggestions] = useState([]);
    const [signatureSuggestions, setSignatureSuggestions] = useState([]);
    const previewRef = useRef(null);
    const shouldScrollRef = useRef(false);

    const currentPeriod = data?.data?.[getNumberFiliereActif];
    const questionLookup = buildQuestionLookup(currentPeriod);
    let visible = isQuestionVisible(questionInfo, {
        dataFiltres: currentPeriod?.filtres,
        questionLookup
    });
    //const questions = data.data[getNumberFiliereActif].criteres[getNumberCritereActif].sousCriteres[sousCritereIndex].questions;

    const getIbanCompactValue = (value) => String(value || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 34);

    const formatIbanDisplayValue = (value) => {
        const compact = getIbanCompactValue(value);
        return compact.replace(/(.{4})(?=.)/g, '$1 ').trim();
    };

    const getBicCompactValue = (value) => String(value || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 11);

    const formatBicDisplayValue = (value) => getBicCompactValue(value);

    const getValueById = (id, field = 'value') => {
        if (!data || !data.data) return '';

        for (const dossier of data.data) {
            if (!dossier.criteres) continue;
            for (const critere of dossier.criteres) {
                if (!critere.sousCriteres) continue;
                for (const sousCritere of critere.sousCriteres) {
                    if (!sousCritere.questions) continue;
                    const question = sousCritere.questions.find(q => q.id === id);
                    if (question) {
                        if (field === 'code_postal') return question.code_postal || '';
                        return question[field] || question.valueName || question.value || '';
                    }
                }
            }
        }
        return '';
    };

    const emptyFormValues = () => ({
        nom: '',
        prenom: '',
        adresse: '',
        codePostal: '',
        ville: '',
        pays: '',
        iban: formatIbanDisplayValue(getValueById(332)),
        bic: formatBicDisplayValue(getValueById(333)),
        villeSignature: ''
    });

    const normalizeText = (value) => String(value || '').trim();

    const normalizePaysValue = (value) => {
        const trimmed = normalizeText(value);
        if (!trimmed) return '';
        const match = PAYS_OPTIONS.find(
            (option) => option.value === trimmed || option.text === trimmed
        );
        return match ? match.text : trimmed;
    };

    const getGarantMandatValues = () => {
        const isRlpGarantValue = normalizeText(getValueById(311)).toLowerCase();
        const isRlpGarant = !isRlpGarantValue || isRlpGarantValue === 'oui';
        const nom = isRlpGarant ? getValueById(132) : getValueById(313);
        const prenom = isRlpGarant ? getValueById(133) : getValueById(314);
        const adresse = isRlpGarant
            ? [getValueById(134), getValueById(135)].filter(Boolean).join(' ').trim()
            : getValueById(315);
        const codePostal = isRlpGarant
            ? getValueById(136, 'code_postal')
            : getValueById(316, 'code_postal');
        const ville = isRlpGarant
            ? (getValueById(136, 'valueName') || getValueById(136))
            : (getValueById(316, 'valueName') || getValueById(316));
        const pays = isRlpGarant ? getValueById(137) : getValueById(317);

        return {
            nom,
            prenom,
            adresse,
            codePostal,
            ville,
            pays: normalizePaysValue(pays),
            iban: formatIbanDisplayValue(getValueById(332)),
            bic: formatBicDisplayValue(getValueById(333)),
            villeSignature: ville
        };
    };

    const formatMandatFormValues = (values) => ({
        ...values,
        nom: formatNom(values.nom),
        prenom: formatPrenom(values.prenom),
        iban: formatIbanDisplayValue(values.iban),
        bic: formatBicDisplayValue(values.bic),
        pays: normalizePaysValue(values.pays)
    });

    const getDefaultMandatFormState = () => {
        const guarantorValues = formatMandatFormValues(getGarantMandatValues());
        const hasPrefillData = [
            guarantorValues.nom,
            guarantorValues.prenom,
            guarantorValues.adresse,
            guarantorValues.codePostal,
            guarantorValues.ville,
            guarantorValues.pays
        ].some((value) => normalizeText(value));

        if (!hasPrefillData) {
            return {
                payeurGarantStatus: '',
                values: emptyFormValues()
            };
        }

        return {
            payeurGarantStatus: 'est-garant',
            values: guarantorValues
        };
    };

    const removeAccents = (str) => String(str || '')
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

    const normalizeVilleSearch = (value) => removeAccents(String(value || '')
        .toLowerCase()
        .replace(/-/g, ' ')
        .replace(/\bsur\b/g, '/')
        .replace(/\bst\b/g, 'saint')
        .replace(/\bnd\b/g, 'notre dame'));

    const filterVilles = (inputValue) => {
        if (!inputValue || villes.length === 0) return [];
        const input = normalizeVilleSearch(inputValue);
        const words = input.split(' ').filter(Boolean);
        if (words.length === 0) return [];

        return villes.filter((ville) => {
            const label = normalizeVilleSearch(ville?.[3]);
            return words.every((word) => label.includes(word));
        }).slice(0, 10);
    };

    const normalizeVilleName = (value) => removeAccents(String(value || '')).toUpperCase();

    const formatPrenom = (value) => {
        let cleanedValue = String(value || '')
            .toLowerCase()
            .replace(/[^a-zA-ZÀ-ÖØ-öø-ÿ' \-^¨]/g, '');

        if (cleanedValue.length > 0) {
            const firstChar = cleanedValue.charAt(0);
            if (firstChar === '-' || firstChar === "'") {
                cleanedValue = cleanedValue.slice(1);
            }

            cleanedValue =
                cleanedValue.charAt(0).toLocaleUpperCase('fr') + cleanedValue.slice(1);

            cleanedValue = cleanedValue.replace(
                /([-'\s])([a-zA-ZÀ-ÖØ-öø-ÿ])/g,
                (match, sep, char) => sep + char.toLocaleUpperCase('fr')
            );
        }

        return cleanedValue;
    };

    const formatNom = (value) => {
        return String(value || '')
            .replace(/[^a-zA-ZÀ-ÖØ-öø-ÿ' \-^¨]/g, '')
            .toUpperCase();
    };

    const formatMandatValue = (fieldName, value) => {
        if (fieldName === 'prenom') return formatPrenom(value);
        if (fieldName === 'nom') return formatNom(value);
        if (fieldName === 'codePostal') {
            return String(value || '').replace(/\D/g, '').slice(0, 5);
        }
        if (fieldName === 'iban') return formatIbanDisplayValue(value);
        if (fieldName === 'bic') return formatBicDisplayValue(value);
        return value;
    };

    const isValidIBAN = (ibanRaw) => {
        let iban = String(ibanRaw || '').replace(/\s+/g, '').toUpperCase();
        if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/.test(iban)) {
            return false;
        }
        const rearranged = iban.slice(4) + iban.slice(0, 4);

        let numericIBAN = "";
        for (const char of rearranged) {
            if (char >= "A" && char <= "Z") {
                numericIBAN += (char.charCodeAt(0) - 55).toString();
            } else {
                numericIBAN += char;
            }
        }

        let remainder = "";
        for (let i = 0; i < numericIBAN.length; i += 7) {
            const block = remainder + numericIBAN.substring(i, i + 7);
            remainder = (parseInt(block, 10) % 97).toString();
        }

        return parseInt(remainder, 10) === 1;
    };

    const isVilleKnown = (villeName) => {
        if (!villeName || villes.length === 0) return true;
        const normalizedVille = normalizeVilleName(villeName);
        return villes.some((ville) => normalizeVilleName(ville?.[0]) === normalizedVille);
    };

    const getVilleError = (values) => {
        const villeName = normalizeText(values?.ville);
        if (!villeName) return '';
        if (!isVilleKnown(villeName)) {
            return "Ville inconnue. Merci de sélectionner une ville dans la liste.";
        }
        if (!values?.codePostal) {
            return "Merci de sélectionner une ville dans la liste pour préremplir le code postal.";
        }
        return '';
    };

    const getVilleSignatureError = (value) => {
        const villeName = normalizeText(value);
        if (!villeName) return '';
        if (!isVilleKnown(villeName)) {
            return "Ville inconnue. Merci de sélectionner une ville dans la liste.";
        }
        return '';
    };

    const getMandatFieldError = (fieldName, rawValue) => {
        const value = String(rawValue || '');
        if (!value) return '';

        if (fieldName === 'iban') {
            const compact = value.replace(/\s+/g, '').toUpperCase();
            if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/.test(compact)) {
                return "Format IBAN invalide (lettres et chiffres uniquement).";
            }
            return isValidIBAN(compact)
                ? ''
                : "IBAN invalide (contrôle MOD 97 échoué).";
        }

        if (fieldName === 'bic') {
            const compact = value.toUpperCase().replace(/\s+/g, "");
            if (!/^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(compact)) {
                return "Le BIC doit contenir 8 ou 11 caractères alphanumériques.";
            }
            if (!/^[A-Z]{4}/.test(compact)) {
                return "Le BIC doit commencer par 4 lettres (code banque).";
            }
            const country = compact.substring(4, 6);
            if (!/^[A-Z]{2}$/.test(country)) {
                return "Le BIC doit contenir un code pays valide (2 lettres).";
            }
            return '';
        }

        return '';
    };

    const getMandatErrors = (values) => {
        const nextErrors = {
            nom: '',
            prenom: '',
            adresse: '',
            codePostal: '',
            ville: '',
            pays: '',
            iban: '',
            bic: '',
            villeSignature: ''
        };

        nextErrors.iban = getMandatFieldError('iban', values.iban);
        nextErrors.bic = getMandatFieldError('bic', values.bic);
        nextErrors.ville = getVilleError(values);
        nextErrors.villeSignature = getVilleSignatureError(values.villeSignature);
        return nextErrors;
    };

    const getEffectiveMandatValues = () => {
        const values = formValues;
        return {
            nom: normalizeText(values.nom),
            prenom: normalizeText(values.prenom),
            adresse: normalizeText(values.adresse),
            codePostal: normalizeText(values.codePostal),
            ville: normalizeText(values.ville),
            pays: normalizeText(values.pays),
            iban: getIbanCompactValue(values.iban),
            bic: getBicCompactValue(values.bic),
            villeSignature: normalizeText(values.villeSignature)
        };
    };

    const getMissingMandatFields = (effectiveValues) => {
        const fields = [
            { label: 'Nom', value: effectiveValues.nom },
            { label: 'Prénom', value: effectiveValues.prenom },
            { label: 'Adresse', value: effectiveValues.adresse },
            { label: 'Ville', value: effectiveValues.ville },
            { label: 'Pays', value: effectiveValues.pays },
            { label: 'IBAN', value: effectiveValues.iban },
            { label: 'BIC', value: effectiveValues.bic },
            { label: 'Ville de signature', value: effectiveValues.villeSignature },
        ];

        return fields
            .filter((field) => !normalizeText(field.value))
            .map((field) => field.label);
    };

    const getInvalidMandatFields = (effectiveValues) => {
        const errors = getMandatErrors(effectiveValues);
        const fields = [
            { label: 'IBAN', key: 'iban' },
            { label: 'BIC', key: 'bic' },
            { label: 'Ville', key: 'ville' },
            { label: 'Ville de signature', key: 'villeSignature' }
        ];

        return fields
            .filter((field) => Boolean(errors[field.key]))
            .map((field) => field.label);
    };

    useEffect(() => {
        if (!isModalOpen || villes.length > 0) return;
        let isMounted = true;

        const fetchVilles = async () => {
            try {
                const response = await fetch('/backend/api/villes');
                const payload = await response.json();
                if (isMounted) setVilles(Array.isArray(payload) ? payload : []);
            } catch (error) {
                console.error('Erreur lors du chargement des villes :', error);
            }
        };

        fetchVilles();

        return () => {
            isMounted = false;
        };
    }, [isModalOpen, villes.length]);

    useEffect(() => {
        if (!isModalOpen || villes.length === 0) return;
        setFormErrors(getMandatErrors(formValues));
        // eslint-disable-next-line
    }, [villes.length, isModalOpen]);

    useEffect(() => {
        axios.get(`/backend/check-file`, {
            params: {
                etudiant: etudiant,
                fileName: fileName
            }
        })
            .then((response) => {
                if (response.data.exists) {
                    updateQuestionValue(sousCritereIndex, questionIndex, response.data.exists);
                    setFileURL(`/backend/static/${etudiant}/${fileName}`);
                } else {
                    updateQuestionValue(sousCritereIndex, questionIndex, "");
                    setFileURL(null);
                }
            })
            .catch(() => {
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (fileURL && previewRef.current && shouldScrollRef.current) {
            previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            shouldScrollRef.current = false;
        }
    }, [fileURL]);

    // Fonction pour télécharger le Mandat SEPA pré-rempli
    const handleDownloadMandatSEPA = async () => {
        const effectiveValues = getEffectiveMandatValues();
        const missingFields = getMissingMandatFields(effectiveValues);
        const invalidFields = getInvalidMandatFields(effectiveValues);

        setFormErrors(getMandatErrors(effectiveValues));

        if (missingFields.length > 0) {
            setDownloadError(`Champs manquants : ${missingFields.join(', ')}. Merci de compléter tous les champs du dossier avant de télécharger ce document.`);
            return;
        }

        if (invalidFields.length > 0) {
            setDownloadError(`Champs invalides : ${invalidFields.join(', ')}. Merci de corriger ces informations avant de télécharger ce document.`);
            return;
        }

        setIsDownloading(true);
        setDownloadError('');
        try {
            const response = await axios.post(
                `/backend/api/generate-mandat-sepa/${etudiant}`,
                {
                    overrides: effectiveValues
                },
                { responseType: 'blob' }
            );

            // Créer un lien de téléchargement
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `MandatSEPA_${etudiant}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erreur lors du téléchargement du Mandat SEPA:', error);
            alert('Erreur lors du téléchargement du Mandat SEPA. Veuillez réessayer.');
            setIsModalOpen(false);
        } finally {
            setIsDownloading(false);
        }
    };

    const openModal = () => {
        const defaultState = getDefaultMandatFormState();
        setFormValues(defaultState.values);
        setPayeurGarantStatus(defaultState.payeurGarantStatus);
        setIsModalOpen(true);
        setDownloadError('');
        setFormErrors(getMandatErrors(defaultState.values));
        setCitySuggestions([]);
        setSignatureSuggestions([]);
    };

    const closeModal = () => {
        if (isDownloading) return;
        setIsModalOpen(false);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        const formattedValue = name === 'pays'
            ? normalizePaysValue(value)
            : formatMandatValue(name, value);
        setFormValues((prev) => {
            const nextValues = { ...prev, [name]: formattedValue };
            setFormErrors(getMandatErrors(nextValues));
            return nextValues;
        });
        if (downloadError) setDownloadError('');
    };

    const handleVilleSearchChange = (event) => {
        const { value } = event.target;
        const formattedValue = formatMandatValue('ville', value);
        setFormValues((prev) => {
            const shouldClearCodePostal = normalizeText(prev.ville) !== normalizeText(formattedValue);
            const nextValues = {
                ...prev,
                ville: formattedValue,
                codePostal: shouldClearCodePostal ? '' : prev.codePostal
            };
            setFormErrors(getMandatErrors(nextValues));
            return nextValues;
        });
        setCitySuggestions(filterVilles(value));
        if (downloadError) setDownloadError('');
    };

    const handleVilleSignatureSearchChange = (event) => {
        handleInputChange(event);
        const nextSuggestions = filterVilles(event.target.value);
        setSignatureSuggestions(nextSuggestions);
    };

    const handleVillePick = (ville) => {
        setFormValues((prev) => {
            const nextValues = {
                ...prev,
                ville: ville?.[0] || '',
                codePostal: ville?.[2] || ''
            };
            setFormErrors(getMandatErrors(nextValues));
            return nextValues;
        });
        setCitySuggestions([]);
        if (downloadError) setDownloadError('');
    };

    const handleVilleSignaturePick = (ville) => {
        setFormValues((prev) => {
            const nextValues = {
                ...prev,
                villeSignature: ville?.[0] || ''
            };
            setFormErrors(getMandatErrors(nextValues));
            return nextValues;
        });
        setSignatureSuggestions([]);
        if (downloadError) setDownloadError('');
    };

    const handlePayeurGarantStatusChange = (event) => {
        const status = event.target.value;
        const nextValues = status === 'est-garant' ? getGarantMandatValues() : emptyFormValues();
        const formattedValues = formatMandatFormValues(nextValues);
        setPayeurGarantStatus(status);
        setFormValues(formattedValues);
        setFormErrors(getMandatErrors(formattedValues));
        setCitySuggestions([]);
        setSignatureSuggestions([]);
        if (downloadError) setDownloadError('');
    };

    const handleFileChange = async (event) => {
        const selectedFile = event.target.files[0];

        if (selectedFile) {
            const fileSizeInMB = selectedFile.size / (1024 * 1024);
            if (fileSizeInMB > 1.5) {
                setIsErrorVisible(true);
                event.target.value = "";
                return;
            }

            setIsErrorVisible(false);
            let convertedFile = selectedFile;

            if (convertedFile) {
                const accepted = await handleFileUpload(convertedFile);
                if (accepted) {
                    shouldScrollRef.current = true;
                    updateQuestionValue(sousCritereIndex, questionIndex, true);
                    setTimeout(() => {
                        loadImageAsBlob();
                    }, 1000);
                } else {
                    event.target.value = "";
                }
            }
        }
    };

    const loadImageAsBlob = async () => {
        const imageUrl = `/backend/static/${etudiant}/${fileName}`;

        try {
            const response = await fetch(imageUrl);
            if (!response.ok) throw new Error("Impossible de charger l'image.");

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            setFileURL(blobUrl);
        } catch (error) {
            console.error("Erreur lors du chargement de l'image :", error);
        }

        return () => {
            if (fileURL) URL.revokeObjectURL(fileURL);
        };
    };

    const handleFileUpload = async (selectedFile) => {
        if (!selectedFile) {
            alert('Please select a file first!');
            return false;
        }

        setIsUploading(true);
        setUploadProgress(0);

        const fileExtension = selectedFile.name.split('.').pop();
        const newFileName = `${questionInfo.nom}.${fileExtension}`;

        const formData = new FormData();
        formData.append("file", selectedFile, newFileName);
        formData.append('etudiant', etudiant);

        try {
            await axios.post('/backend/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (event) => {
                    if (!event.total) return;
                    const nextProgress = Math.min(100, Math.round((event.loaded / event.total) * 100));
                    setUploadProgress(nextProgress);
                }
            });

            setUploadProgress(100);
            await addItemToToken(etudiant, newFileName);

            await convertDocument(newFileName);
        } catch (error) {
            console.error('Error uploading file:', error);
            return false;
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
        return true;
    };

    const addItemToToken = async (token, newItem) => {
        try {
            await axios.post('/backend/api/add-item-to-token', {
                token,
                newItem
            });
        } catch (error) {
            console.error('Erreur lors de l\'ajout de l\'élément:', error.response?.data || error.message);
        }
    };

    const convertDocument = async (uploadedFileName) => {
      try {
        await axios.post("/backend/convert-document", {
          etudiant,
          fileName: uploadedFileName,
          outputName: fileName
        });
      } catch (error) {
        console.error("Erreur lors de la conversion de la pièce:", error.response?.data || error.message);
      }
    };

    if (visible) {
        return (
            <div className="question">
                {isUploading && (
                    <div className="uploadOverlay">
                        <div className="uploadOverlayCard">
                            <div className="uploadSpinner" />
                            <div>{Lang === "en" ? "Uploading file..." : "Téléversement du fichier..."}</div>
                            <div className="uploadProgress">{uploadProgress}%</div>
                            <div className="uploadProgressBar">
                                <div className="uploadProgressBarFill" style={{ width: `${uploadProgress}%` }} />
                            </div>
                        </div>
                    </div>
                )}
                <label className="questionTitle">
                    {questionInfo.etiquette} :{questionInfo.required === "required" ? " *" : ""}
                </label>
                <div className="questionUpload">
                    <div className="questionvertical">
                        {/* Bouton de téléchargement du Mandat SEPA pré-rempli */}
                        <div style={{ marginBottom: '15px' }}>
                            <button
                                type="button"
                                onClick={openModal}
                                disabled={isDownloading}
                                style={{
                                    backgroundColor: '#0066cc',
                                    color: 'white',
                                    padding: '12px 24px',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: isDownloading ? 'wait' : 'pointer',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    opacity: isDownloading ? 0.7 : 1
                                }}
                            >
                                <span>📄</span>
                                {isDownloading ? 'Génération en cours...' : 'Télécharger mon Mandat SEPA pré-rempli'}
                            </button>
                            <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                                ⚠️ Téléchargez votre mandat SEPA pré-rempli, signez-le, puis déposez-le ci-dessous.
                            </p>
                        </div>

                        {/* Champ d'upload */}
                        <div className="fileInputWrapper">
                            <input
                                id={questionInfo.id}
                                name={questionInfo.id}
                                type="file"
                                accept={questionInfo.accept}
                                onChange={handleFileChange}
                                className="fileInput"
                            />
                            <label htmlFor={questionInfo.id} className="fileInputLabel">
                                {Lang === "en" ? "Choose a file" : "Choisir un fichier"}
                            </label>
                        </div>

                        {isErrorVisible && (
                            <p className="uploadStatus uploadStatus--file" style={{ color: "red" }}>
                                {Lang === "en" ? "The file is too large!" : "Le fichier est trop volumineux !"}
                            </p>
                        )}

                        {fileURL && (
                            <div ref={previewRef}>
                                <img src={fileURL} alt="File preview" />
                            </div>
                        )}
                    </div>
                    <div className="QuestionHelpUpload">
                        <p><u>Un seul fichier accepté. Merci de faire attention à la taille du fichier</u></p>
                        <p>Formats acceptés : JPG, PNG, PDF.</p>
                        <p>Taille de fichier maximale : 1,5 Mo.</p>
                        <p>- Pour fusionner et/ou compresser des pdf : <a target="_blank" rel="noreferrer" href="https://www.ilovepdf.com/fr">ilovepdf</a></p>
                        <p>- Pour compresser des images : <a target="_blank" rel="noreferrer" href="https://compressjpeg.com/fr">compressjpeg</a></p>
                        <p style={{ color: "#FF0000", fontSize: "16px", fontWeight: "normal" }}>
                            Merci de vérifier l'exactitude des informations et de signer le document avant de le déposer.
                        </p>
                    </div>
                </div>
                {isModalOpen && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2000,
                            padding: 'clamp(12px, 3vw, 28px)'
                        }}
                        onClick={closeModal}
                    >
                        <div
                            style={{
                                backgroundColor: '#fff',
                                width: '100%',
                                maxWidth: '960px',
                                borderRadius: '10px',
                                padding: '24px clamp(16px, 3vw, 32px)',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: '85vh',
                                overflowY: 'auto'
                            }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <h3 style={{ marginTop: 0, textAlign: 'center' }}>Informations pour le mandat SEPA</h3>
                            <div style={{ display: 'grid', gap: '12px', width: '100%', maxWidth: '760px', margin: '0 auto' }}>
                                <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'grid', gap: '6px' }}>
                                    <legend className="questionTitle" style={{ marginBottom: '4px' }}>
                                        Lien entre payeur et garant financier :
                                    </legend>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="radio"
                                            name="payeur-garant"
                                            value="non-garant"
                                            checked={payeurGarantStatus === 'non-garant'}
                                            onChange={handlePayeurGarantStatusChange}
                                        />
                                        Le payeur n'est pas le garant financier
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="radio"
                                            name="payeur-garant"
                                            value="est-garant"
                                            checked={payeurGarantStatus === 'est-garant'}
                                            onChange={handlePayeurGarantStatusChange}
                                        />
                                        Le payeur est le garant financier
                                    </label>
                                </fieldset>
                                <label className="questionTitle" htmlFor="mandat-nom">Nom :</label>
                                <input
                                    id="mandat-nom"
                                    type="text"
                                    name="nom"
                                    placeholder="Nom"
                                    value={formValues.nom}
                                    onChange={handleInputChange}
                                    className="text"
                                    style={{ width: '100%' }}
                                />
                                {formErrors.nom && (
                                    <div className="field-error" style={{ color: "red", fontSize: "0.85rem" }}>
                                        {formErrors.nom}
                                    </div>
                                )}
                                <label className="questionTitle" htmlFor="mandat-prenom">Prénom :</label>
                                <input
                                    id="mandat-prenom"
                                    type="text"
                                    name="prenom"
                                    placeholder="Prénom"
                                    value={formValues.prenom}
                                    onChange={handleInputChange}
                                    className="text"
                                    style={{ width: '100%' }}
                                />
                                {formErrors.prenom && (
                                    <div className="field-error" style={{ color: "red", fontSize: "0.85rem" }}>
                                        {formErrors.prenom}
                                    </div>
                                )}
                                <label className="questionTitle" htmlFor="mandat-adresse">Adresse :</label>
                                <input
                                    id="mandat-adresse"
                                    type="text"
                                    name="adresse"
                                    placeholder="Adresse"
                                    value={formValues.adresse}
                                    onChange={handleInputChange}
                                    className="text"
                                    style={{ width: '100%' }}
                                />
                                {formErrors.adresse && (
                                    <div className="field-error" style={{ color: "red", fontSize: "0.85rem" }}>
                                        {formErrors.adresse}
                                    </div>
                                )}
                                <label className="questionTitle" htmlFor="mandat-ville">Ville :</label>
                                <div style={{ display: 'grid', gap: '6px' }}>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            id="mandat-ville"
                                            type="text"
                                            name="ville"
                                            placeholder="Ville"
                                            value={formValues.ville}
                                            onChange={handleVilleSearchChange}
                                            className="text"
                                            style={{ width: '100%' }}
                                        />
                                        {citySuggestions.length > 0 && (
                                            <ul
                                                style={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    right: 0,
                                                    top: '100%',
                                                    zIndex: 10,
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '6px',
                                                    marginTop: '4px',
                                                    listStyle: 'none',
                                                    padding: '4px 0',
                                                    maxHeight: '180px',
                                                    overflowY: 'auto'
                                                }}
                                            >
                                                {citySuggestions.map((ville) => (
                                                    <li
                                                        key={`${ville?.[1]}-${ville?.[2]}`}
                                                        style={{ padding: '6px 10px', cursor: 'pointer' }}
                                                        onMouseDown={() => handleVillePick(ville)}
                                                    >
                                                        {ville?.[3]}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                {formErrors.ville && (
                                    <div className="field-error" style={{ color: "red", fontSize: "0.85rem" }}>
                                        {formErrors.ville}
                                    </div>
                                )}
                                <label className="questionTitle" htmlFor="mandat-pays">Pays :</label>
                                <select
                                    id="mandat-pays"
                                    name="pays"
                                    value={formValues.pays}
                                    onChange={handleInputChange}
                                    className="text"
                                    style={{ width: '100%' }}
                                >
                                    <option value="">-</option>
                                    {PAYS_OPTIONS.map((option) => (
                                        <option key={option.id} value={option.text}>
                                            {option.text}
                                        </option>
                                    ))}
                                </select>
                                {formErrors.pays && (
                                    <div className="field-error" style={{ color: "red", fontSize: "0.85rem" }}>
                                        {formErrors.pays}
                                    </div>
                                )}
                                <label className="questionTitle" htmlFor="mandat-iban">IBAN :</label>
                                <input
                                    id="mandat-iban"
                                    type="text"
                                    name="iban"
                                    placeholder="IBAN"
                                    maxLength={42}
                                    value={formValues.iban}
                                    onChange={handleInputChange}
                                    className="text"
                                    style={{ width: '100%' }}
                                />
                                {formErrors.iban && (
                                    <div className="field-error" style={{ color: "red", fontSize: "0.85rem" }}>
                                        {formErrors.iban}
                                    </div>
                                )}
                                <label className="questionTitle" htmlFor="mandat-bic">BIC :</label>
                                <input
                                    id="mandat-bic"
                                    type="text"
                                    name="bic"
                                    placeholder="BIC"
                                    maxLength={11}
                                    value={formValues.bic}
                                    onChange={handleInputChange}
                                    className="text"
                                    style={{ width: '100%' }}
                                />
                                {formErrors.bic && (
                                    <div className="field-error" style={{ color: "red", fontSize: "0.85rem" }}>
                                        {formErrors.bic}
                                    </div>
                                )}
                                <label className="questionTitle" htmlFor="mandat-ville-signature">Ville de signature :</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        id="mandat-ville-signature"
                                        type="text"
                                        name="villeSignature"
                                        placeholder="Ville de signature"
                                        value={formValues.villeSignature}
                                        onChange={handleVilleSignatureSearchChange}
                                        className="text"
                                        style={{ width: '100%' }}
                                    />
                                    {signatureSuggestions.length > 0 && (
                                        <ul
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                right: 0,
                                                top: '100%',
                                                zIndex: 10,
                                                backgroundColor: '#fff',
                                                border: '1px solid #ccc',
                                                borderRadius: '6px',
                                                marginTop: '4px',
                                                listStyle: 'none',
                                                padding: '4px 0',
                                                maxHeight: '180px',
                                                overflowY: 'auto'
                                            }}
                                        >
                                            {signatureSuggestions.map((ville) => (
                                                <li
                                                    key={`${ville?.[1]}-${ville?.[2]}`}
                                                    style={{ padding: '6px 10px', cursor: 'pointer' }}
                                                    onMouseDown={() => handleVilleSignaturePick(ville)}
                                                >
                                                    {ville?.[3]}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                {formErrors.villeSignature && (
                                    <div className="field-error" style={{ color: "red", fontSize: "0.85rem" }}>
                                        {formErrors.villeSignature}
                                    </div>
                                )}
                            </div>

                            {downloadError && (
                                <div style={{ margin: '16px auto 0', width: '100%', maxWidth: '760px', color: '#b00020' }}>
                                    {downloadError}
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '10px', marginTop: '20px' }}>
                                <button type="button" onClick={closeModal} disabled={isDownloading}>
                                    Annuler
                                </button>
                                <button type="button" onClick={handleDownloadMandatSEPA} disabled={isDownloading}>
                                    {isDownloading ? 'Génération en cours...' : 'Télécharger le mandat'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    } else {
        return null;
    }
}

export default QuestionMandatSEPA;
